// "Excel export" and "Condition report" buttons for checks and damage. Used on the Fleet History screen and the
// Damage Timeline. A vehicle number limits both to that vehicle; the Excel export with no vehicle covers the fleet.
import React, { useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { FileSpreadsheet, Printer, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { formatDate, formatDateTime } from "@/lib/dates";
import { cachedTenantBranding } from "@/lib/branding";
import { loadDamageData } from "@/lib/damageData";
import { buildConditionReportHtml, buildExportSheets, type SheetRow } from "@/lib/damageReports";
import { localIsoDay } from "@/lib/vehicleChecks";

interface Props {
  /** One vehicle, or null for the whole fleet (Excel only). */
  vehicleNumber: string | null;
  dateFrom?: Date;
  dateTo?: Date;
  className?: string;
}

const sheetFrom = (rows: SheetRow[], emptyHeader: string): XLSX.WorkSheet => {
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ [emptyHeader]: "Nothing to show for this selection." }]);
  const headers = Object.keys(rows[0] ?? { [emptyHeader]: "" });
  ws["!cols"] = headers.map((h) => ({ wch: Math.min(60, Math.max(h.length + 2, ...rows.slice(0, 200).map((r) => String(r[h] ?? "").length + 2))) }));
  return ws;
};

const DamageReportButtons: React.FC<Props> = ({ vehicleNumber, dateFrom, dateTo, className }) => {
  const [busy, setBusy] = useState<"excel" | "report" | null>(null);

  const exportExcel = async () => {
    setBusy("excel");
    try {
      const data = await loadDamageData({ vehicle: vehicleNumber });
      const sheets = buildExportSheets(data, {
        formatDay: formatDate,
        dateFrom: dateFrom ? localIsoDay(dateFrom) : null,
        dateTo: dateTo ? localIsoDay(dateTo) : null,
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheetFrom(sheets.damageItems, "Damage items"), "Damage items");
      XLSX.utils.book_append_sheet(wb, sheetFrom(sheets.damageLog, "Damage log"), "Damage log");
      XLSX.utils.book_append_sheet(wb, sheetFrom(sheets.inspections, "Checks"), "Checks");
      XLSX.writeFile(wb, `checks-and-damage-${vehicleNumber ? `vehicle-${vehicleNumber}` : "fleet"}-${localIsoDay(new Date())}.xlsx`);
      toast({
        title: "Export ready",
        description: `${sheets.damageItems.length} damage item(s), ${sheets.inspections.length} check(s).`,
      });
    } catch (e: any) {
      toast({ title: "Couldn't create the export", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const openReport = async () => {
    if (!vehicleNumber) return;
    // Opened straight away (while this is still the click) so the browser doesn't block it as a pop-up.
    const win = window.open("", "_blank");
    if (!win) {
      toast({ title: "Pop-up blocked", description: "Allow pop-ups for this site to open the report.", variant: "destructive" });
      return;
    }
    win.document.write("<p style=\"font-family:Arial;margin:24px\">Building the report...</p>");
    setBusy("report");
    try {
      const [data, vehicleRes] = await Promise.all([
        loadDamageData({ vehicle: vehicleNumber, withPhotos: true }),
        supabase.from("vehicles").select("vehicle_number, registration, make, model, year").eq("vehicle_number", vehicleNumber).maybeSingle(),
      ]);
      const brand = cachedTenantBranding();
      const html = buildConditionReportHtml({
        vehicle: vehicleRes.data ?? { vehicle_number: vehicleNumber },
        data,
        companyName: brand?.display_name || brand?.tenant_name || "Vehicle",
        generatedOn: formatDateTime(new Date()),
        formatDay: formatDate,
        formatDateTime,
      });
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (e: any) {
      win.close();
      toast({ title: "Couldn't build the report", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={className ?? "flex flex-wrap gap-2"}>
      <Button variant="outline" size="sm" onClick={exportExcel} disabled={busy !== null} title={vehicleNumber ? "Damage and checks for this vehicle" : "Damage and checks for the whole fleet"}>
        {busy === "excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
        Excel export
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={openReport}
        disabled={busy !== null || !vehicleNumber}
        title={vehicleNumber ? "Printable report with photos and damage marks" : "Choose a vehicle first"}
      >
        {busy === "report" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
        Condition report
      </Button>
    </div>
  );
};

export default DamageReportButtons;
